import { Box, Button, Card, Collapse, Flex, Grid, MultiSelect, TagsInput, Text, Textarea, TextInput } from '@mantine/core';
import {
  IconChevronDown,
  IconChevronUp,
  IconCircle,
  IconCircleDotted,
  IconPlus,
  IconSettings,
  IconTrash,
} from '@tabler/icons-react';
import { useState } from 'react';
import CustomFormLabel from './CustomFormLabel';

const GenerativePromptEditor = ({
  brandContext,
  generatedContent,
  generateContent,
  loading,
  setCardBodyOpen,
  handleContextChange,
}) => {
  const inputContextKeys = Object.keys(generatedContent?.inputContext || { topic: true });
  const defaultSections = inputContextKeys.reduce((acc, key) => ({ ...acc, [key]: true }), {});

  const [addedSections, setAddedSections] = useState(defaultSections);
  const [expandedSections, setExpandedSections] = useState({ topic: true });

  const maxAddedFields = {
    email: 8,
    facebookAd: 6,
    blogPost: 7,
    viber: 7,
    sms: 7,
    whatsApp: 7,
  };

  const inputDisabled = !!generatedContent.data;
  const marketingChannel = generatedContent?.marketingChannel;

  const handleToggle = (sectionName) => {
    setExpandedSections((prevState) => ({ ...prevState, [sectionName]: !prevState[sectionName] }));
  };

  const isSectionOpen = (sectionName) => {
    return expandedSections[sectionName];
  };

  const handleAddSection = (sectionName) => {
    setAddedSections((prevState) => ({ ...prevState, [sectionName]: !prevState[sectionName] }));
    handleToggle(sectionName);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (inputDisabled) {
      return;
    }

    if (!value.length) {
      return handleContextChange({ [name]: null }, true);
    }

    handleContextChange({ [name]: value }, true);
  };

  const handleRemoveSection = (sectionName) => {
    if (addedSections[sectionName]) {
      setAddedSections((prevAddedSections) => {
        const newAddedSections = { ...prevAddedSections };
        delete newAddedSections[sectionName];
        return newAddedSections;
      });

      if (generatedContent.inputContext && generatedContent?.inputContext?.[sectionName]?.length) {
        handleContextChange({ [sectionName]: null }, true);
      }
    }

    if (expandedSections[sectionName]) {
      setExpandedSections((prevExpandedSections) => {
        const newExpandedSections = { ...prevExpandedSections };
        delete newExpandedSections[sectionName];
        return newExpandedSections;
      });
    }
  };

  const generateButton = (
    <Button
      fullWidth
      color="cyan"
      onClick={async () => {
        setExpandedSections({});
        setCardBodyOpen(false);
        await generateContent();
      }}
      disabled={!generatedContent?.inputContext?.topic || loading}
    >
      <IconSettings size={16} /> {loading ? 'Generating' : 'Generate Content'}
    </Button>
  );

  return (
    <>
      <div style={{ padding: 0, marginBottom: 'var(--mantine-spacing-sm)' }}>
        <Card mb={0} withBorder>
          <CustomFormLabel
            name={'Topic *'}
            inputName={'topic'}
            iconSetting={generatedContent?.inputContext?.topic ? IconCircle : IconCircleDotted}
            handleToggle={handleToggle}
            isSectionOpen={isSectionOpen}
          />
          <Collapse expanded={isSectionOpen('topic')}>
            <Box mb="md">
              <Text size="sm">What is the topic?</Text>
              <Textarea
                name="topic"
                value={generatedContent?.inputContext?.topic || ''}
                onChange={(event) => handleInputChange({ target: { name: 'topic', value: event.target.value } })}
                disabled={inputDisabled}
                autosize
                minRows={2}
              />
              <Text size="xs" c="dimmed">
                * Topic context is required for optimal results.
              </Text>
            </Box>
          </Collapse>
        </Card>
      </div>
      {addedSections['brandAudiences'] && (
        <div style={{ padding: 0, marginBottom: 'var(--mantine-spacing-sm)' }}>
          <Card mb={0} withBorder>
            {' '}
            <CustomFormLabel
              name={'Audience'}
              inputName={'brandAudiences'}
              iconSetting={generatedContent?.inputContext?.brandAudiences?.length > 0 ? IconCircle : IconCircleDotted}
              handleToggle={handleToggle}
              isSectionOpen={isSectionOpen}
            />
            <Grid.Col span={12}>
              <Collapse expanded={isSectionOpen('brandAudiences')}>
                <div>
                  <Text size="sm">Who is the target audience?</Text>
                  <MultiSelect
                    data={brandContext?.audiences?.value?.map((audience) => ({
                      label: audience.title,
                      value: audience.title,
                    }))}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: 'brandAudiences', value },
                      })
                    }
                    value={generatedContent?.inputContext?.brandAudiences}
                    disabled={inputDisabled}
                  />
                  <div style={{ marginBottom: 'var(--mantine-spacing-xs)' }}>
                    <Text size="xs" c="dimmed">
                      Using <b>one</b> Audience for context is recommended for optimal results.
                    </Text>
                  </div>
                  {!inputDisabled && (
                    <IconTrash
                      size={16}
                      onClick={() => handleRemoveSection('brandAudiences')}
                      className="button-element"
                      style={{ float: 'left', marginBottom: 'var(--mantine-spacing-md)', cursor: 'pointer' }}
                    />
                  )}
                </div>
              </Collapse>
            </Grid.Col>
          </Card>
        </div>
      )}
      {addedSections['brandVoices'] && (
        <div style={{ padding: 0, marginBottom: 'var(--mantine-spacing-sm)' }}>
          <Card mb={0} withBorder>
            <CustomFormLabel
              name={'Brand Voice'}
              inputName={'brandVoices'}
              iconSetting={generatedContent?.inputContext?.brandVoices?.length > 0 ? IconCircle : IconCircleDotted}
              handleToggle={handleToggle}
              isSectionOpen={isSectionOpen}
            />
            <Grid.Col span={12}>
              <Collapse expanded={isSectionOpen('brandVoices')}>
                <div>
                  <Text size="sm"> What should the brand voice in the content be?</Text>
                  <TagsInput
                    name="brandVoices"
                    mb="sm"
                    data={brandContext?.voices?.value || []}
                    onChange={(brandVoices) =>
                      handleInputChange({
                        target: { name: 'brandVoices', value: brandVoices },
                      })
                    }
                    value={generatedContent?.inputContext?.brandVoices || []}
                    disabled={inputDisabled}
                  />
                  {!inputDisabled && (
                    <IconTrash
                      size={16}
                      onClick={() => handleRemoveSection('brandVoices')}
                      className="button-element"
                      style={{ float: 'left', marginBottom: 'var(--mantine-spacing-md)', cursor: 'pointer' }}
                    />
                  )}
                </div>
              </Collapse>
            </Grid.Col>
          </Card>
        </div>
      )}
      {addedSections['brandTones'] && (
        <div style={{ padding: 0, marginBottom: 'var(--mantine-spacing-sm)' }}>
          <Card mb={0} withBorder>
            {' '}
            <CustomFormLabel
              name={'Brand Tone'}
              inputName={'brandTones'}
              iconSetting={generatedContent?.inputContext?.brandTones?.length > 0 ? IconCircle : IconCircleDotted}
              handleToggle={handleToggle}
              isSectionOpen={isSectionOpen}
            />
            <Grid.Col span={12}>
              <Collapse expanded={isSectionOpen('brandTones')}>
                <div>
                  <Text size="sm"> What should the brand tone in the content be?</Text>
                  <TagsInput
                    mb="sm"
                    data={brandContext?.tones?.value || []}
                    onChange={(tones) => handleInputChange({ target: { name: 'brandTones', value: tones } })}
                    value={generatedContent?.inputContext?.brandTones || []}
                    disabled={inputDisabled}
                  />
                  {!inputDisabled && (
                    <IconTrash
                      size={16}
                      onClick={() => handleRemoveSection('brandTones')}
                      className="button-element"
                      style={{ float: 'left', marginBottom: 'var(--mantine-spacing-md)', cursor: 'pointer' }}
                    />
                  )}
                </div>
              </Collapse>
            </Grid.Col>
          </Card>
        </div>
      )}
      {addedSections['brandArchetype'] && (
        <div style={{ padding: 0, marginBottom: 'var(--mantine-spacing-sm)' }}>
          <Card mb={0} withBorder>
            {' '}
            <CustomFormLabel
              name={'Brand Archetype'}
              inputName={'brandArchetype'}
              iconSetting={
                generatedContent?.inputContext?.['brandArchetype'] || brandContext?.archetypes?.value
                  ? IconCircle
                  : IconCircleDotted
              }
              handleToggle={handleToggle}
              isSectionOpen={isSectionOpen}
            />
            <Grid.Col span={12}>
              <Collapse expanded={isSectionOpen('brandArchetype')}>
                <div>
                  <Text size="sm"> What human-like character traits would you like your brand to embody?</Text>
                  <TextInput
                    name="brandArchetype"
                    disabled={inputDisabled}
                    value={
                      generatedContent?.inputContext?.['brandArchetype']
                        ? generatedContent.inputContext['brandArchetype']
                        : brandContext?.archetypes?.value || ''
                    }
                    onChange={(event) =>
                      handleInputChange({ target: { name: 'brandArchetype', value: event.target.value } })
                    }
                    mb="md"
                  />
                  {!inputDisabled && (
                    <IconTrash
                      size={16}
                      onClick={() => handleRemoveSection('brandArchetype')}
                      className="button-element"
                      style={{ float: 'left', marginBottom: 'var(--mantine-spacing-md)', cursor: 'pointer' }}
                    />
                  )}
                </div>
              </Collapse>
            </Grid.Col>
          </Card>
        </div>
      )}
      {addedSections['subject'] && (
        <div style={{ padding: 0, marginBottom: 'var(--mantine-spacing-sm)' }}>
          <Card mb={0} withBorder>
            {' '}
            <CustomFormLabel
              name={'Subject'}
              inputName={'subject'}
              iconSetting={generatedContent?.inputContext?.subject ? IconCircle : IconCircleDotted}
              handleToggle={handleToggle}
              isSectionOpen={isSectionOpen}
            />
            <Grid.Col span={12}>
              <Collapse expanded={isSectionOpen('subject')}>
                <div>
                  <Text size="sm"> What should the subject line be?</Text>
                  <TextInput
                    name="subject"
                    value={generatedContent?.inputContext?.subject || ''}
                    onChange={(event) => handleInputChange({ target: { name: 'subject', value: event.target.value } })}
                    mb="md"
                    disabled={inputDisabled}
                  />
                  {!inputDisabled && (
                    <IconTrash
                      size={16}
                      onClick={() => handleRemoveSection('subject')}
                      className="button-element"
                      style={{ float: 'left', marginBottom: 'var(--mantine-spacing-md)', cursor: 'pointer' }}
                    />
                  )}
                </div>
              </Collapse>
            </Grid.Col>
          </Card>
        </div>
      )}
      {addedSections['callToAction'] && (
        <div style={{ padding: 0, marginBottom: 'var(--mantine-spacing-sm)' }}>
          <Card mb={0} withBorder>
            {' '}
            <CustomFormLabel
              name={'Call To Action'}
              inputName={'callToAction'}
              iconSetting={generatedContent?.inputContext?.callToAction ? IconCircle : IconCircleDotted}
              handleToggle={handleToggle}
              isSectionOpen={isSectionOpen}
            />
            <Grid.Col span={12}>
              <Collapse expanded={isSectionOpen('callToAction')}>
                <div>
                  <Text size="sm"> What should the primary call to action be?</Text>
                  <TextInput
                    name="callToAction"
                    disabled={inputDisabled}
                    value={generatedContent?.inputContext?.callToAction || ''}
                    onChange={(event) =>
                      handleInputChange({ target: { name: 'callToAction', value: event.target.value } })
                    }
                    mb="md"
                  />
                  {!inputDisabled && (
                    <IconTrash
                      size={16}
                      onClick={() => handleRemoveSection('callToAction')}
                      className="button-element"
                      style={{ float: 'left', marginBottom: 'var(--mantine-spacing-md)', cursor: 'pointer' }}
                    />
                  )}
                </div>
              </Collapse>
            </Grid.Col>
          </Card>
        </div>
      )}
      {addedSections['customInformation'] && (
        <div style={{ padding: 0, marginBottom: 'var(--mantine-spacing-sm)' }}>
          <Card mb={0} withBorder>
            {' '}
            <CustomFormLabel
              name={'Custom Information'}
              inputName={'customInformation'}
              iconSetting={generatedContent?.inputContext?.customInformation ? IconCircle : IconCircleDotted}
              handleToggle={handleToggle}
              isSectionOpen={isSectionOpen}
            />
            <Grid.Col span={12}>
              <Collapse expanded={isSectionOpen('customInformation')}>
                <div>
                  <Text size="sm"> What custom information would you like to include?</Text>
                  <Textarea
                    name="customInformation"
                    value={generatedContent?.inputContext?.customInformation || ''}
                    onChange={(event) =>
                      handleInputChange({ target: { name: 'customInformation', value: event.target.value } })
                    }
                    mb="md"
                    disabled={inputDisabled}
                    autosize
                    minRows={2}
                  />
                  {!inputDisabled && (
                    <IconTrash
                      size={16}
                      onClick={() => handleRemoveSection('customInformation')}
                      className="button-element"
                      style={{ float: 'left', marginBottom: 'var(--mantine-spacing-md)', cursor: 'pointer' }}
                    />
                  )}
                </div>
              </Collapse>
            </Grid.Col>
          </Card>
        </div>
      )}
      {inputDisabled === false && (
        <>
          <hr />
          {maxAddedFields[marketingChannel] !== Object.keys(addedSections).length && (
            <div style={{ padding: 0, marginBottom: 'var(--mantine-spacing-sm)' }}>
              <Card style={{ boxShadow: '0 0 0 2px var(--mantine-primary-color-filled)' }} mb="xs" withBorder>
                <Text
                  fw={500}
                  onClick={() => handleToggle('moreFields')}
                  style={{ cursor: 'pointer' }}
                  mb="xs"
                  component="div"
                >
                  <Flex align="center">
                    <IconPlus size={16} className="rotate-on-hover" style={{ float: 'left', marginRight: 8 }} />
                    <span style={{ flexGrow: 1 }}> Add More Context</span>
                    {isSectionOpen('moreFields') ? (
                      <IconChevronUp size={16} style={{ float: 'right' }} />
                    ) : (
                      <IconChevronDown size={16} style={{ float: 'right' }} />
                    )}
                  </Flex>
                </Text>
                <Grid.Col span={12}>
                  <Collapse expanded={isSectionOpen('moreFields')}>
                    <div>
                      {!addedSections.brandAudiences && (
                        <Card mb="xs" onClick={() => handleAddSection('brandAudiences')} withBorder>
                          <div style={{ paddingTop: 'var(--mantine-spacing-sm)', cursor: 'pointer' }}>
                            <Text size="sm" mb="xs">
                              Audiences
                            </Text>
                            <Text size="xs" c="dimmed">
                              Who is the target audience?
                            </Text>
                          </div>
                        </Card>
                      )}
                      {!addedSections.brandVoices && (
                        <Card mb="xs" onClick={() => handleAddSection('brandVoices')} withBorder>
                          <div style={{ paddingTop: 'var(--mantine-spacing-sm)', cursor: 'pointer' }}>
                            <Text size="sm" mb="xs">
                              Brand Voice
                            </Text>
                            <Text size="xs" c="dimmed">
                              What should the brand voice in the content be?
                            </Text>
                          </div>
                        </Card>
                      )}
                      {!addedSections.brandTones && (
                        <Card mb="xs" onClick={() => handleAddSection('brandTones')} withBorder>
                          <div style={{ paddingTop: 'var(--mantine-spacing-sm)', cursor: 'pointer' }}>
                            <Text size="sm" mb="xs">
                              Brand Tone
                            </Text>
                            <Text size="xs" c="dimmed">
                              What should the brand tone in the content be?
                            </Text>{' '}
                          </div>
                        </Card>
                      )}
                      {!addedSections.brandArchetype && (
                        <Card mb="xs" onClick={() => handleAddSection('brandArchetype')} withBorder>
                          <div style={{ paddingTop: 'var(--mantine-spacing-sm)', cursor: 'pointer' }}>
                            <Text size="sm" mb="xs">
                              Brand Archetype
                            </Text>
                            <Text size="xs" c="dimmed">
                              What human-like character traits would you like your brand to embody?{' '}
                            </Text>
                          </div>
                        </Card>
                      )}
                      {!addedSections.subject && marketingChannel === 'email' && (
                        <Card mb="xs" onClick={() => handleAddSection('subject')} withBorder>
                          <div style={{ paddingTop: 'var(--mantine-spacing-sm)', cursor: 'pointer' }}>
                            <Text size="sm" mb="xs">
                              Subject
                            </Text>
                            <Text size="xs" c="dimmed">
                              What should the subject line be?
                            </Text>
                          </div>
                        </Card>
                      )}
                      {!addedSections.callToAction && marketingChannel !== 'facebookAd' && (
                        <Card mb="xs" onClick={() => handleAddSection('callToAction')} withBorder>
                          <div style={{ paddingTop: 'var(--mantine-spacing-sm)', cursor: 'pointer' }}>
                            {' '}
                            <Text size="sm" mb="xs">
                              Call To Action
                            </Text>
                            <Text size="xs" c="dimmed">
                              What should the primary call to action be?
                            </Text>
                          </div>
                        </Card>
                      )}
                      {!addedSections.customInformation && (
                        <Card mb="xs" onClick={() => handleAddSection('customInformation')} withBorder>
                          <div style={{ paddingTop: 'var(--mantine-spacing-sm)', cursor: 'pointer' }}>
                            {' '}
                            <Text size="sm" mb="xs">
                              Custom Information
                            </Text>
                            <Text size="xs" c="dimmed">
                              What custom information would you like to include?
                            </Text>
                          </div>
                        </Card>
                      )}
                    </div>
                  </Collapse>
                </Grid.Col>
              </Card>
            </div>
          )}
        </>
      )}

      {!generatedContent.data && generateButton}
    </>
  );
};

export default GenerativePromptEditor;
